import { PlusOutlined } from '@ant-design/icons'

import { BlankIcon, EmptyState, PageHeader } from '@lmc/ui-kit'
import { Button } from '@lmc/ui-kit/src/general/Button'
import { useAbilityAccess } from 'components/AccessControl'
import { LoadingScreen } from 'components/generic'
import useResidentFullName from 'hooks/useResidentFullName'
import { NAMESPACE } from 'locale'
import { goalsConstants } from 'locale/goals'
import { isBrowser, isTablet } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { commonPatterns } from '../../../../utils/enhancedTestIds'
import GoalsList from './components/GoalsList/GoalsList'
import useFilteredGoals from './components/useGoals/useFilteredGoals'

const isDesktop = isBrowser || isTablet

const GoalsScreen = ({ testId }: { testId?: string } = {}) => {
  const { id: residentID } = useParams()
  const navigate = useNavigate()

  const residentName = useResidentFullName(residentID)

  const { t } = useTranslation(NAMESPACE.GOALS)
  const { goals, doneGoals, archivedGoals, isLoading } =
    useFilteredGoals(residentID)

  const hasRolePermission = useAbilityAccess([
    {
      i: 'create',
      this: 'Goal'
    }
  ])

  const baseId = testId || 'goals-screen'

  return (
    <div data-testid={baseId}>
      <PageHeader
        sticky
        title={
          residentName
            ? t(goalsConstants.GOALS_FOR, { name: residentName })
            : t(goalsConstants.GOALS)
        }
        // This is a reused code snippet for the back button that's inside a header that's already inside the codebase. Might be a good idea to refactor it at some point
        onBack={
          !isDesktop
            ? () =>
                navigate(`/residents/${residentID}/profile`, { replace: true })
            : undefined
        }
        data-testid={`${baseId}-header`}
      >
        <Link to="create" style={{ color: 'white' }}>
          <Button
            variant="primary"
            icon={<PlusOutlined />}
            data-cy="add-goal-btn"
            disabledByPermission={!hasRolePermission}
            data-testid={commonPatterns.button(baseId, 'add-goal')}
          >
            {t(goalsConstants.ADD_GOAL)}
          </Button>
        </Link>
      </PageHeader>
      {isLoading ? (
        <LoadingScreen data-testid={`${baseId}-loading`} />
      ) : (
        <ListsWrapper
          data-cy="goals-list-container"
          data-testid={`${baseId}-list-container`}
        >
          {goals.length > 0 ? (
            <GoalsList residentID={residentID} items={goals} testId={baseId} />
          ) : (
            <EmptyState
              title={t(goalsConstants.GOALS_EMPTY_LIST_TITLE)}
              description={t(goalsConstants.GOALS_EMPTY_LIST_DESCRIPTION)}
              icon={<BlankIcon />}
              data-testid={`${baseId}-empty-state`}
            />
          )}
          <SecondaryGoalsLists data-testid={`${baseId}-secondary-lists`}>
            {doneGoals.length > 0 && (
              <GoalsList
                residentID={residentID}
                items={doneGoals}
                title={t(goalsConstants.COMPLETED)}
                testId={baseId}
              />
            )}
            {archivedGoals.length > 0 && (
              <GoalsList
                residentID={residentID}
                items={archivedGoals}
                title={t(goalsConstants.ARCHIVED)}
                testId={baseId}
              />
            )}
          </SecondaryGoalsLists>
        </ListsWrapper>
      )}
    </div>
  )
}

const ListsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 80px;
  margin-top: 12px;
`

const SecondaryGoalsLists = styled(ListsWrapper)`
  gap: 60px;
`

export default GoalsScreen
